<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="500">
        <v-form v-model="formData.valid" class="mb-3" validate-on="input" @submit.prevent="submitForm">
            <v-card>
                <v-card-title id="heading" class="d-flex justify-space-between">
                    <h4>Export Logs</h4>
                    <v-btn density="compact" icon="mdi-close" variant="flat" @click="$emit('update:modelValue',false)"/>
                </v-card-title>

                <v-container>
                    <v-alert v-if="errors.length" class="mb-3 bg-error" closable close-label="Close Alert"
                             density="compact" variant="tonal">
                        <p v-for="error in errors">{{ error }}</p>
                    </v-alert>
                    <v-row>
                        <v-col v-if="assets" cols="12">
                            <v-autocomplete
                                v-model="formData['asset_id']"
                                :items="assets"
                                :rules="[vRules.required]"
                                density="compact"
                                hide-details
                                item-text="label"
                                item-title="label"
                                item-value="id"
                                label="Assets"/>
                        </v-col>
                        <v-col cols="12">
                            <v-date-input
                                v-model="formData['from_date']"
                                append-inner-icon="$calendar"
                                clearable
                                density="compact"
                                hide-actions
                                hide-details
                                label="From Date"
                                prepend-icon=""
                                @click:clear="formData['from_date']=null"/>
                        </v-col>
                        <v-col cols="12">
                            <v-date-input
                                v-model="formData['to_date']"
                                append-inner-icon="$calendar"
                                clearable
                                density="compact"
                                hide-actions
                                label="To Date"
                                prepend-icon=""
                                @click:clear="formData['to_date']=null"/>
                        </v-col>
                    </v-row>
                </v-container>

                <v-card-actions class="px-4 d-flex justify-end">
                    <v-btn size="small" type="button"
                           @click="$emit('update:modelValue',false)">
                        Close
                    </v-btn>
                    <v-btn :disabled="!formData['asset_id']" color="primary" size="small" type="submit">Export</v-btn>
                </v-card-actions>
            </v-card>
        </v-form>
    </v-dialog>
</template>

<script setup>
import {ref, reactive} from "vue";
import {send} from "@/composables/apiClient";
import {useLoadingStore} from "@/stores/loadingStore";
import {VDateInput} from "vuetify/lib/labs/VDateInput";
import {useSnackbarStore} from "@/stores/snackBarStore";

const loadingStore = useLoadingStore();
const snackbar = useSnackbarStore();
const props = defineProps({
    modelValue: Boolean,
    asset: {
        type: Object,
        default: null
    },
    assets: {
        type: Array,
        default: null
    },
});

const formData = reactive({
    to_date: null,
    from_date: null,
    asset_id: props.asset?.id ?? null,
});
const emits = defineEmits(['update:modelValue']);
const dialogSwitch = ref(props.modelValue);
const errors = ref([]);

const within60Days = (from_date_str, to_date_str) => {
    // Parse strings into Date objects
    const from_date = new Date(from_date_str);
    const to_date = new Date(to_date_str);

    // Guard against invalid dates
    if (isNaN(from_date) || isNaN(to_date)) return false;

    // Normalize to same time (optional if times differ); here we zero out to midnight
    from_date.setHours(0, 0, 0, 0);
    to_date.setHours(0, 0, 0, 0);

    // Milliseconds in a day (using 24h days)
    const MS_PER_DAY = 24 * 60 * 60 * 1000;

    // Absolute difference in days
    const diffDays = Math.abs(to_date - from_date) / MS_PER_DAY;

    // Check if diff is <= 60 days
    return diffDays <= 60;
}
const submitForm = () => {
    errors.value = [];
    if ((formData['from_date'] && !formData['to_date']) || (!formData['from_date'] && formData['to_date'])) {
        snackbar.showSnackbar("One of the date is missing", 'error');
        return;
    }
    if (formData['from_date'] && formData['to_date']) {
        if (!within60Days(formData['from_date'], formData['to_date'])) {
            snackbar.showSnackbar("Max 60 days request is permitted", 'error');
            return;
        }
    }
    loadingStore.showLoading('Exporting...', 'Please wait while we are processing your request');
    send(`post`, `admin/device-logs-export`, formData, {responseType: 'arraybuffer'})
        .then((response) => {
            const contentType = response.headers['content-type'];

            if (response.status === 200) {
                if (contentType && contentType.includes('application/json')) {
                    // Decode JSON error from ArrayBuffer
                    const decoder = new TextDecoder('utf-8');
                    const jsonText = decoder.decode(new Uint8Array(response.data));
                    const jsonData = JSON.parse(jsonText);

                    if (jsonData?.message) {
                        snackbar.showSnackbar(jsonData.message, 'error');
                        return;
                    }
                }

                // Create and trigger download
                const blob = new Blob([response.data], {type: 'application/gzip'});
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `device_raw_packets_${Date.now()}.csv.gz`;
                link.click();
                URL.revokeObjectURL(url);
                emits('update:modelValue', false);
            }
        })
        .catch(function (errorResponse) {
            console.log(errorResponse);
            snackbar.showSnackbar('Something went wrong', 'error');
            document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
        }).finally(() => loadingStore.hideLoading());
}
</script>
